import { useState, useRef, useCallback, memo } from "react";
import PortraitMedia from "../../PortraitMedia";

export interface PortraitSlotProps {
  /** Current portrait — a server URL (/uploads/portraits/…). */
  value: string | null;
  /** POST target: /api/sessions/:sid/sheets/:sheetId/portrait?participantId=… */
  uploadUrl: string;
  /** Clear the portrait (caller sends sheet:set-portrait null). */
  onClear: () => void;
  size?: { width: number; height: number };
  placeholder?: string;
  disabled?: boolean;
  label?: string;
}

const DEFAULT_SIZE = { width: 140, height: 180 };
// Mirrors the server contract in server/src/routes/upload.router.ts
// (PORTRAIT_MAX_SIZE / PORTRAIT_MIME): 10 MB, images + animated webm.
const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,video/webm";

/**
 * PortraitSlot — server-upload portrait in the ImageSlot paper shell.
 *
 * Reuses the upload contract of the SheetModal-era PortraitUpload component
 * (multipart POST → server stores the file under uploads/portraits/ and
 * broadcasts sheet:portrait-updated, which useMultiplayerSync already
 * applies), so the sheet carries a short URL instead of a base64 blob.
 * This is why the limit is 10 MB here while ImageSlot — which inlines a
 * data-URL into sheet state and pushes it through every WS broadcast —
 * has to cap at 2 MB. Use ImageSlot only where no upload endpoint exists
 * (demo/singleton mode).
 *
 * The new value arrives via the server echo (sheet:portrait-updated →
 * adapter remap → `value` prop); there is no optimistic local preview —
 * the spinner overlay covers the round-trip.
 */
function PortraitSlotImpl({
  value,
  uploadUrl,
  onClear,
  size = DEFAULT_SIZE,
  placeholder = "?",
  disabled = false,
  label,
}: PortraitSlotProps) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleUpload = useCallback(
    async (file: File) => {
      if (file.size > MAX_SIZE) {
        setError(`Файл слишком большой (макс. ${Math.round(MAX_SIZE / 1048576)} МБ)`);
        return;
      }
      setError(null);
      setUploading(true);
      try {
        const form = new FormData();
        form.append("portrait", file);
        const resp = await fetch(uploadUrl, { method: "POST", body: form });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({ error: "Не удалось загрузить" }));
          setError(data.error || "Не удалось загрузить");
        }
      } catch {
        setError("Сеть недоступна — портрет не загружен");
      } finally {
        setUploading(false);
      }
    },
    [uploadUrl],
  );

  const handleClick = useCallback(() => {
    if (!uploading) inputRef.current?.click();
  }, [uploading]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleUpload(file);
      e.target.value = "";
    },
    [handleUpload],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleUpload(file);
    },
    [handleUpload],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onClear();
    },
    [onClear],
  );

  const rootStyle: React.CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }
    : {};

  return (
    <div style={rootStyle}>
      {label && (
        <div
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            letterSpacing: ".16em",
            textTransform: "uppercase",
            color: "var(--paper-ink-2)",
            marginBottom: 6,
          }}
        >
          {label}
        </div>
      )}
      <div
        style={{
          width: size.width,
          height: size.height,
          border: dragOver
            ? "2px dashed var(--paper-accent)"
            : "2px dashed var(--paper-rule)",
          background: dragOver ? "rgba(139,37,21,.05)" : "var(--paper-bg)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: uploading ? "wait" : "pointer",
          position: "relative",
          overflow: "hidden",
          transition: "border-color .12s",
          flexShrink: 0,
        }}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          style={{ display: "none" }}
          onChange={handleInputChange}
        />
        {value !== null ? (
          <PortraitMedia
            src={value}
            alt=""
            className="h-full w-full"
          />
        ) : (
          <div
            style={{
              font: "400 36px 'Fraunces', serif",
              fontStyle: "italic",
              color: "var(--paper-ink-2)",
              userSelect: "none",
              pointerEvents: "none",
            }}
          >
            {placeholder}
          </div>
        )}
        {uploading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(244,239,227,.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              font: "500 11px 'JetBrains Mono', monospace",
              color: "var(--paper-ink)",
            }}
          >
            …
          </div>
        )}
        {value !== null && !uploading && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Удалить портрет"
            style={{
              position: "absolute",
              top: 4,
              right: 4,
              width: 20,
              height: 20,
              border: "none",
              background: "rgba(27,23,20,.65)",
              color: "var(--paper-bg)",
              font: "500 13px sans-serif",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        )}
      </div>
      {error !== null && (
        <div
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            color: "var(--paper-accent)",
            marginTop: 4,
            maxWidth: size.width,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}

PortraitSlotImpl.displayName = "PortraitSlot";

export const PortraitSlot = memo(PortraitSlotImpl);
